import {
  AloftArea,
  Improvements,
  ManufacturedHomeFeatures,
  ManufacturedHomeFoundationDisplay,
  ORDER_FEATURES,
  OrderFeature,
  PropertyData,
  getFoundationDisplay,
  isDefined,
} from '@aloft/toolkit-shared';

import { PropertyGridSection } from '../../../components/PropertyGrid/columnSections/PropertyGridSection.tsx';
import {
  getAloftAreaJSONPropertyGridField,
  getAreaAndQualityFeatureAdjustmentGridField,
  getNumberPropertyGridField,
} from '../../../components/PropertyGrid/fields/utils.tsx';
import {
  PropertyGridField,
  PropertyGridSectionProps,
} from '../../../components/PropertyGrid/types.ts';
import { selectAllImprovements } from '../../../redux/features/selectors/propertyDataSelectors/improvementsSelectors.ts';
import { useAppSelector } from '../../../redux/hooks.ts';
import { getFeatureQualityLabel } from '../../../redux/utils/getFeatureQualityLabel.ts';

type ImprovementField = PropertyGridField<PropertyData['editableData']['improvements']>;

export const AdjustmentsGridDepreciatedCostSection = (props: PropertyGridSectionProps) => {
  const allImprovements = useAppSelector(selectAllImprovements);

  const improvementsFieldName = 'editableData.improvements';

  const qualityField: ImprovementField = {
    fieldName: improvementsFieldName,
    label: 'Quality',
    manualAdjustmentKey: 'quality',
    toDisplayValue: (improvements?: Improvements) => {
      const { craftsmanQuality, propertyType, uadQuality } = improvements ?? {};

      let foundationLabel: ManufacturedHomeFoundationDisplay | undefined;
      if (propertyType === 'manufacturedHome') {
        const { foundation } = improvements as ManufacturedHomeFeatures;
        foundationLabel = getFoundationDisplay(foundation);
      }

      const foundationDisplayLabel = foundationLabel ? ` (${foundationLabel})` : '';
      return `Q${uadQuality ?? '-'}/${craftsmanQuality ?? '-'}${foundationDisplayLabel}`;
    },
  };

  const conditionField: ImprovementField = {
    fieldName: improvementsFieldName,
    label: 'Condition',
    manualAdjustmentKey: 'condition',
    toDisplayValue: (improvements) => {
      const { effectiveAge, uadCondition } = improvements ?? {};
      return `C${uadCondition ?? '-'}/E${effectiveAge ?? '-'}`;
    },
  };

  const bathCountField: ImprovementField = {
    fieldName: improvementsFieldName,
    label: 'Bath Count',
    manualAdjustmentKey: 'bathAboveGrade',
    toDisplayValue: (improvements) => {
      const { fullBaths, halfBaths } = improvements ?? {};
      if (fullBaths === undefined && halfBaths === undefined) return undefined;

      return `${fullBaths ?? 0}.${halfBaths ?? 0}`;
    },
  };

  const garageCarportField: ImprovementField = {
    fieldName: improvementsFieldName,
    label: 'Garage / Carport',
    manualAdjustmentKey: 'garageCarport',
    toDisplayValue: (improvements) => {
      const { garage, carport } = improvements ?? {};
      if (!garage && !carport) return undefined;

      const { area: garageArea } = garage ?? {};
      const { area: carportArea } = carport ?? {};

      const garageAreaDisplay = garageArea
        ? `${AloftArea.schema.parse(garageArea).getSiteAreaString('')} Garage`
        : undefined;
      const carportAreaDisplay = carportArea
        ? `${AloftArea.schema.parse(carportArea).getSiteAreaString('')} Carport`
        : undefined;

      return [garageAreaDisplay, carportAreaDisplay].filter(isDefined).join(';');
    },
  };

  const porchPatioDeckField: ImprovementField = {
    fieldName: improvementsFieldName,
    label: 'Porch / Patio / Deck',
    manualAdjustmentKey: 'porchPatioDeck',
    toDisplayValue: (improvements) => {
      if (!improvements) return undefined;

      return [ORDER_FEATURES.porch, ORDER_FEATURES.patio, ORDER_FEATURES.deck]
        .filter((feature) => feature.hasFeature(improvements))
        .map((feature) => feature.label)
        .join(';');
    },
  };

  const atticField: ImprovementField = {
    fieldName: improvementsFieldName,
    label: 'Attic',
    manualAdjustmentKey: 'attic',
    toDisplayValue: (improvements) => {
      const { attic } = improvements ?? {};
      if (!attic) return undefined;
      if (!attic.area) return undefined;

      return AloftArea.schema.parse(attic.area).getSiteAreaString('');
    },
  };

  const fireplaceField: ImprovementField = {
    fieldName: improvementsFieldName,
    label: 'Fireplace',
    manualAdjustmentKey: 'fireplace',
    toDisplayValue: (improvements) => {
      const { fireplace } = improvements ?? {};
      if (!fireplace) return undefined;
      if (!fireplace.type || !fireplace.count) return undefined;

      return `${fireplace.type} (${fireplace.count})`;
    },
  };

  const barnField: ImprovementField = getAreaAndQualityFeatureAdjustmentGridField('barn');

  const aduField: ImprovementField = {
    fieldName: improvementsFieldName,
    label: 'ADU',
    manualAdjustmentKey: 'adu',
    toDisplayValue: (improvements) => {
      const { adu } = improvements ?? {};
      if (!adu) return undefined;
      if (!adu.area || !adu.quality) return undefined;

      const { area, quality } = adu;

      const areaString = AloftArea.schema.parse(area).getSiteAreaString('');
      return `${areaString}/QL${quality}`;
    },
  };

  const basementField: ImprovementField = {
    fieldName: improvementsFieldName,
    label: 'Basement',
    manualAdjustmentKey: 'basement',
    toDisplayValue: (improvements) => {
      const totalArea = improvements?.basement?.totalArea;
      if (!totalArea) return undefined;

      return AloftArea.schema.parse(totalArea).getSiteAreaString('');
    },
  };

  const poolSpaField: ImprovementField = {
    fieldName: improvementsFieldName,
    label: 'Pool / Spa',
    manualAdjustmentKey: 'poolSpa',
    toDisplayValue: (improvements) => {
      const { pool, spa } = improvements ?? {};
      if (!pool && !spa) return undefined;

      let poolDisplay: string | undefined;
      let spaDisplay: string | undefined;
      if (pool && pool.hasFeature && isDefined(pool.area)) {
        const { area, quality } = pool;

        const areaString = AloftArea.schema.parse(area).getSiteAreaString('');
        const qualityLabel = getFeatureQualityLabel('pool', quality, true);
        poolDisplay = `${areaString} Pool/${qualityLabel}`;
      }
      if (spa && spa.hasFeature && isDefined(spa.area)) {
        const { area, quality } = spa;

        const areaString = AloftArea.schema.parse(area).getSiteAreaString('');
        const qualityLabel = getFeatureQualityLabel('spa', quality, true);
        spaDisplay = `${areaString} Spa/${qualityLabel}`;
      }

      return [poolDisplay, spaDisplay].filter(isDefined).join('; ');
    },
  };

  const shedField: ImprovementField = getAreaAndQualityFeatureAdjustmentGridField('shed');
  const workshopField: ImprovementField = getAreaAndQualityFeatureAdjustmentGridField('workshop');

  const manualCostField: ImprovementField = {
    fieldName: improvementsFieldName,
    label: 'Manual Cost',
    toDisplayValue: (improvements) => {
      const { manualCostDisplayName } = improvements ?? {};
      return manualCostDisplayName || undefined;
    },
  };

  const featureFields: ImprovementField[] = [];

  const shouldDisplayFeatureField = (featureDependencies: OrderFeature[]) =>
    featureDependencies.some((feature) =>
      allImprovements.some((improvements) =>
        ORDER_FEATURES[feature].hasFeature(improvements as Improvements),
      ),
    );

  if (shouldDisplayFeatureField(['garage', 'carport'])) featureFields.push(garageCarportField);
  if (shouldDisplayFeatureField(['porch', 'patio', 'deck'])) {
    featureFields.push(porchPatioDeckField);
  }
  if (shouldDisplayFeatureField(['attic'])) featureFields.push(atticField);
  if (shouldDisplayFeatureField(['fireplace'])) featureFields.push(fireplaceField);
  if (shouldDisplayFeatureField(['barn'])) featureFields.push(barnField);
  if (shouldDisplayFeatureField(['adu'])) featureFields.push(aduField);
  if (shouldDisplayFeatureField(['basement'])) featureFields.push(basementField);
  if (shouldDisplayFeatureField(['pool', 'spa'])) featureFields.push(poolSpaField);
  if (shouldDisplayFeatureField(['shed'])) featureFields.push(shedField);
  if (shouldDisplayFeatureField(['workshop'])) featureFields.push(workshopField);
  if (shouldDisplayFeatureField(['manualCost'])) featureFields.push(manualCostField);

  return (
    <PropertyGridSection
      {...props}
      title="Depreciated Cost Adjustments"
      fields={[
        qualityField,
        conditionField,
        getAloftAreaJSONPropertyGridField('editableData.improvements.gla', 'GLA', 'gla'),
        getNumberPropertyGridField('editableData.improvements.bedrooms', 'Bed Count', 'bedrooms'),
        bathCountField,
        ...featureFields,
      ]}
    />
  );
};
